import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { BottomNav } from './BottomNav';
import { apiFetch } from '../../utils/api';
import { passwordLengthError } from '../../utils/passwordPolicy';
import { PasswordStrength } from '../ui/PasswordStrength';

export function AppLayout({
  user, restaurant, onNavigate,
  onLogout, onSwitchRestaurant, theme, onToggleTheme,
  title, subtitle, actions, children, onSwitchToEmployee,
  allRestaurants, onSelectRestaurantTab, onAdminNotifNavigate,
}) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const prevPath = useRef(location.pathname);

  // Profile panel state
  const [showProfile, setShowProfile] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmNewPw, setConfirmNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState('');

  function openProfile() { setShowProfile(true); }
  function closeProfile() { setShowProfile(false); setChangePwOpen(false); setPwMessage(''); setCurrentPw(''); setNewPw(''); setConfirmNewPw(''); }

  async function handleChangePassword() {
    if (!currentPw || !newPw || !confirmNewPw) { setPwMessage('All fields required.'); return; }
    if (newPw !== confirmNewPw) { setPwMessage('New passwords do not match.'); return; }
    if (passwordLengthError(newPw)) { setPwMessage(passwordLengthError(newPw)); return; }
    setPwSaving(true); setPwMessage('');
    try {
      const data = await apiFetch('/api/employee/profile/password', {
        method: 'PATCH', body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });
      if (data.ok) {
        setPwMessage('✅ Password updated. Other sessions have been signed out.');
        setCurrentPw(''); setNewPw(''); setConfirmNewPw('');
        setTimeout(() => { setPwMessage(''); setChangePwOpen(false); }, 2000);
      } else {
        setPwMessage(data.message || 'Failed to update password.');
      }
    } catch { setPwMessage('Could not connect.'); }
    setPwSaving(false);
  }

  const locationLabel = restaurant?.name || (user?.role === 'admin' ? 'All Locations' : '');

  // Close the mobile menu on navigation; actual data loaders own loading feedback.
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      setMobileOpen(false);
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);

  return (
    <div className="app-layout workspace-ui">

      {/* PROFILE PANEL */}
      {showProfile && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={closeProfile} />
          <div style={{ position: 'relative', width: '100%', maxWidth: '480px', background: 'var(--surface)', borderRadius: '20px 20px 0 0', maxHeight: '85vh', overflowY: 'auto', paddingBottom: 'env(safe-area-inset-bottom)', zIndex: 1 }}>
            {/* Drag handle */}
            <div style={{ padding: '12px 20px 4px', textAlign: 'center' }}>
              <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'var(--border)', margin: '0 auto' }} />
            </div>

            {/* Profile header */}
            <div style={{ padding: '12px 20px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'var(--brand)', color: 'var(--brand-contrast)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '900', flexShrink: 0 }}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '17px', fontWeight: '800', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
                  {locationLabel && <div style={{ fontSize: '12px', color: 'var(--brand)', fontWeight: '600', marginTop: '2px' }}>{locationLabel}</div>}
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', textTransform: 'capitalize' }}>{user?.role}</div>
                </div>
              </div>

              {/* Change Password */}
              <button onClick={() => { setChangePwOpen(o => !o); setPwMessage(''); }} style={{ width: '100%', padding: '11px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)', fontSize: '13px', fontWeight: '600' }}>
                <span>🔑 Change Password</span>
                <span style={{ fontSize: '10px', color: 'var(--text-3)' }}>{changePwOpen ? '▲' : '▼'}</span>
              </button>
              {changePwOpen && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { placeholder: 'Current password', value: currentPw, onChange: setCurrentPw },
                    { placeholder: 'New password (at least 15 characters)', value: newPw, onChange: setNewPw },
                    { placeholder: 'Confirm new password', value: confirmNewPw, onChange: setConfirmNewPw },
                  ].map((f, i) => (
                    <input key={i} type="password" aria-label={f.placeholder} autoComplete={i===0 ? 'current-password' : 'new-password'} placeholder={f.placeholder} value={f.value} onChange={e => f.onChange(e.target.value)} style={{ padding: '10px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '16px', fontFamily: 'inherit', outline: 'none' }} />
                  ))}
                  <PasswordStrength value={newPw} name={user?.name} email={user?.email} />
                  {pwMessage && <div role="status" style={{ fontSize: '12px', fontWeight: '600', color: pwMessage.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{pwMessage}</div>}
                  <button onClick={handleChangePassword} disabled={pwSaving} style={{ padding: '11px', background: pwSaving ? 'var(--bg-secondary)' : '#0ea5e9', border: 'none', borderRadius: '8px', color: pwSaving ? 'var(--text-3)' : '#fff', fontWeight: '700', fontSize: '13px', cursor: pwSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                    {pwSaving ? 'Saving…' : 'Save Password'}
                  </button>
                </div>
              )}
            </div>

            {/* Sign Out */}
            <div style={{ padding: '14px 20px 20px' }}>
              <button onClick={() => { closeProfile(); onLogout(); }} style={{ width: '100%', padding: '14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', color: '#dc2626', fontWeight: '700', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SIDEBAR */}
      <div className={`app-sidebar-wrapper ${mobileOpen ? 'mobile-open' : ''}`}>
        <Sidebar
          user={user}
          onNavigate={onNavigate}
          onLogout={onLogout}
          onOpenProfile={openProfile}
          onSwitchRestaurant={onSwitchRestaurant}
          restaurant={restaurant}
          theme={theme}
          onToggleTheme={onToggleTheme}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(c => !c)}
          onMobileClose={() => setMobileOpen(false)}
          onSwitchToEmployee={undefined}
        />
      </div>

      {/* MOBILE OVERLAY — click to close */}
      {mobileOpen && (
        <div
          className="sidebar-overlay active"
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 99,
          }}
        />
      )}

      {/* MAIN */}
      <main id="main-content" className={`app-main ${collapsed ? 'collapsed' : ''}`}>
        <TopBar
          title={title}
          subtitle={subtitle}
          actions={actions}
          onMenuOpen={() => setMobileOpen(o => !o)}
          onLogout={onLogout}
          onOpenProfile={openProfile}
          user={user}
          restaurantId={restaurant?.id}
          userRole={user?.role}
          userName={user?.name}
          onAdminNotifNavigate={onAdminNotifNavigate}
          onSwitchToEmployee={undefined}
        />
        <div className="context-bar" aria-label="Location and view">
          <label>Location
            {['admin', 'general_manager'].includes(user?.role) ? <select aria-label="Current location" value={restaurant?.id || ''} onChange={event => onSelectRestaurantTab?.(allRestaurants?.find(r => String(r.id) === event.target.value) || null)}>
              <option value="">All locations</option>
              {allRestaurants?.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select> : <strong>{restaurant?.name}</strong>}
          </label>
          {(onSwitchToEmployee || (restaurant && ['admin', 'general_manager'].includes(user?.role))) && <label>View
            <select aria-label="Current view" value={restaurant ? 'operations' : 'overview'} onChange={event => { if (event.target.value === 'employee') onSwitchToEmployee?.(); else if (event.target.value === 'overview') onSelectRestaurantTab?.(null); }}>
              {!restaurant && <option value="overview">All locations</option>}
              {restaurant && <option value="operations">Location view</option>}
              {restaurant && ['admin', 'general_manager'].includes(user?.role) && <option value="overview">All locations</option>}
              {onSwitchToEmployee && <option value="employee">Employee view</option>}
            </select>
          </label>}
          <span className="context-role">Signed in as {user?.role === 'general_manager' ? 'General Manager' : user?.role === 'admin' ? 'Admin' : 'Manager'}</span>
        </div>
        <div className="app-content" style={{ position: 'relative' }}>
          {children}
        </div>
      </main>

      {/* BOTTOM NAV (mobile) */}
      <BottomNav restaurant={restaurant} user={user} />

    </div>
  );
}
